import { useEffect, useState } from "react";
import { Trash2, Plus, Info } from "lucide-react";
import AddTeacherModal from "../../modals/AddTeacherModal";
import ConfirmDeleteTeacherModal from "../../modals/ConfirmDeleteTeacherModal";
import { getTeachers } from "../../../utils/getTeachers";
import useUserStore from "../../../stores/user";
import supabase from "../../../utils/supabase";

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDistrictLoading, setIsDistrictLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddTeacherOpen, setIsAddTeacherOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);
  const { user, profile } = useUserStore();
  const [district, setDistrict] = useState(null);
  const [schools, setSchools] = useState([]);

  async function fetchTeachers(search?: string) {
    if (!profile?.district) {
      setTeachers([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const { data, error } = await getTeachers({ search });

    if (error) {
      console.error("Failed to fetch teachers:", error);
      setIsLoading(false);
      return;
    }

    const mapped = (data || []).map((t) => ({
      id: t.id,
      fullname: t.fullname,
      email: t.email,
      role: t.role,
      schoolName: t.schoolName,
      createdAt: new Date(t.created_at).toLocaleDateString(),
    }));

    setTeachers(mapped);
    setIsLoading(false);
  }

  async function fetchDistrict() {
    if (!profile?.district) {
      setIsDistrictLoading(false);
      return;
    }
    setIsDistrictLoading(true);
    const { data, error } = await supabase
      .from("districts")
      .select("id, name")
      .eq("id", profile.district)
      .maybeSingle();

    if (error) {
      console.error("Failed to fetch district:", error);
    }

    setDistrict(data);
    setIsDistrictLoading(false);
  }

  async function fetchSchools() {
    if (!profile?.district) return;
    const { data, error } = await supabase
      .from("schools")
      .select("id, name, district_id")
      .eq("district_id", profile.district);

    if (error) {
      console.error("Failed to fetch schools:", error);
    }

    setSchools(data);
  }

  useEffect(() => {
    fetchDistrict();
    fetchSchools();
  }, [profile]);

  // Initial fetch and debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchTeachers(searchTerm);
      },
      searchTerm ? 500 : 0
    ); // No delay for initial load, 500ms delay for search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, user?.id]);

  const handleDeleteClick = (teacher) => {
    setTeacherToDelete(teacher);
    setIsDeleteModalOpen(true);
  };

  const handleTeacherDeleted = () => {
    // Refresh the teachers list
    fetchTeachers(searchTerm);
  };

  return (
    <div>
      {/* Modals */}
      <AddTeacherModal
        isOpen={isAddTeacherOpen}
        onClose={() => {
          setIsAddTeacherOpen(false);
        }}
        onSuccess={() => {
          setIsAddTeacherOpen(false);
          fetchTeachers(searchTerm);
        }}
        schools={schools}
      />

      <ConfirmDeleteTeacherModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTeacherToDelete(null);
        }}
        teacher={teacherToDelete}
        onTeacherDeleted={handleTeacherDeleted}
      />

      {/* Header */}
      <div className="bg-white border-b shadow-sm p-6 flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Manage teachers in the system.
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            District:{" "}
            {isDistrictLoading
              ? "Loading..."
              : district?.name || "Not assigned"}
          </p>
          {!district?.name && !isDistrictLoading && (
            <p className="text-sm text-yellow-600 mt-1 flex items-center gap-1">
              <Info className="w-4 h-4" />
              Please contact Project Explore <b>Super Admin</b> to get assigned to a
              district in order to manage teachers.
            </p>
          )}
        </div>
        <div className="space-x-2 flex gap-2">
          <button
            onClick={() => setIsAddTeacherOpen(true)}
            disabled={!district?.name}
            className="btn btn-small"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Teacher
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 bg-gray-50">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Teachers
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-lg"
                placeholder="Search by name or email"
                value={searchTerm}
                disabled={!district?.name || isDistrictLoading}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    School
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-sm text-gray-500 py-6 italic"
                    >
                      Loading teachers...
                    </td>
                  </tr>
                ) : teachers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      {searchTerm
                        ? "No teachers found matching your search."
                        : "No teachers found."}
                    </td>
                  </tr>
                ) : (
                  teachers.map((teacher) => (
                    <tr key={teacher.id}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {teacher.fullname}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {teacher.email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {teacher.schoolName}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {teacher.createdAt}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleDeleteClick(teacher)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Delete Teacher"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
